/* istanbul ignore file */

import React, { type ComponentProps, memo } from 'react';
import { StyleSheet, View } from 'react-native';

import { BodyText, Image, theme, TitleText, useResponsive } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationResultsListInfoMessage = memo((props: Props) => {
  const { title, text, illustration, illustrationAriaLabel } = props;

  const { match } = useResponsive();

  // ─── Styles ────────────────────────────────────────────────────────────────

  const containerStyles = [styles.container, match([styles.containerXS, undefined])];

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <View style={containerStyles}>
      <Image
        source={illustration}
        style={styles.illustration}
        aria-label={illustrationAriaLabel}
        contentFit="contain"
      />

      {title ? (
        <TitleText sizeMatch={['18', '24']} style={styles.title}>
          {title}
        </TitleText>
      ) : null}

      {text ? (
        <BodyText sizeMatch={['14', '16']} style={styles.text}>
          {text}
        </BodyText>
      ) : null}
    </View>
  );
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    paddingVertical: theme.spacing['6'],
    paddingHorizontal: theme.spacing['2'],
  },
  containerXS: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingBottom: theme.spacing['10'],
  },
  illustration: {
    width: 240,
    height: 240,
  },
  title: {
    textAlign: 'center',
    marginBottom: theme.spacing['4'],
  },
  text: {
    textAlign: 'center',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  illustration: ComponentProps<typeof Image>['source'];
  illustrationAriaLabel: string;
  title?: string;
  text?: string;
};
